import {
    useContext, useMemo,
    useState,
} from 'react';
import {
    Card, CardBody, CardFooter, CardImg,
    CardSubtitle, CardTitle, Badge, Collapse,
    DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown,
    UncontrolledTooltip,
    FormGroup,
} from 'reactstrap';
import moment from 'moment';
import clsx from 'clsx';
import {
    faCircleNotch, faChevronDown, faChevronUp, faCircleCheck,
    faEllipsisVertical, faEye, faAmbulance, faFlag, faHardHat,
    faMapMarkerAlt, faTrafficLight, faTruck, faUser, faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { getStaticMapUrlForAddress, getCounty } from '@s20/common/util/location';
import { useInstanceSelector, useSelector } from '@s20/hooks/redux.hooks';
import { getCrewLeadLabel, getTenantSetting, getTenantSettings } from '@s20/selectors/features/common.selectors';
import { useFormValues, useSetFieldValue } from '@s20/components/forms/FormContext';
import { Link } from '@s20/components/common/Link';
import { getContrastTextColor } from '@s20/common/util/style';
import { getMapUrl } from '@s20/common/util/dom';
import { pluralize } from '@s20/common/util/l10n';
import { appendTimeZone } from '@s20/common/util/date';
import {
    Flex, FlexEndRow,
    FlexStartRow, FormLabel, IconButton,
} from '../../../../common/forms/FormElements';
import { AppNavPaths, CustomFormContext } from '../../../../Common';
import { CountyLabel } from '../../../../uscounties/CountyLabel';
import CustomForms from '../../../../customForm/CustomForms';
import { DispatchContext } from '../../DispatchContext';
import { TenantSettings } from '../../../../tenant/Tenant';
import { Can } from '../../../../Can';
import { viewEquipmentLog } from '../../../../dispatch/DispatchJobAssignments';
import { ConfirmationStatus } from '../../../../dispatch/Dispatch';
import { InfoItem } from './InfoItem';
import { CancelJob } from './CancelJob';
import { Certification } from './Certification';
import { Employee } from './Resource/Employee';
import { Equipment } from './Resource/Equipment';
import { JobContext, JobSavingContext } from './JobContext';
import { TransferJob } from './TransferJob';
import { FlagJob } from './FlagJob';
import { SetEmergency } from './Emergency';

export const Job = ({ setSelectedWorkflowJob, ...job }) => {
    const { usCounties } = useContext(DispatchContext);
    const crewLead = useSelector(getCrewLeadLabel);
    const [saving, setSaving] = useState(false);

    const timesheetEquipmentAuditEnabled = useInstanceSelector(getTenantSetting, TenantSettings.TimesheetEquipmentAuditEnabled);

    const setFieldValue = useSetFieldValue();

    const selectedDate = useFormValues('selectedDate');
    const selectedJobId = useFormValues('selectedJobId');

    const day = moment(selectedDate).day();

    const tenantSettings = useSelector(getTenantSettings);
    const location = {
        lat: job.latitude,
        lng: job.longitude,
    };

    const assignment = job.assignments[day];
    const {
        canEdit, staff, equipment, isFlagged,
        isEmergency, timesheetId, flags, jobId,
        jobNumber,
    } = assignment;

    const trucks = equipment.filter((eq) => eq.isVehicle);
    const afads = equipment.filter((eq) => eq.equipmentTypeAFADTypeId > 1);

    const county = getCounty(usCounties, {
        usCountyId: job.usCounty?.id,
        contractUnionWorkTypeId: job.contractUnionWorkTypeId,
    });

    const hasAnyStaff = staff.length > 0;
    const hasAnyEquipment = equipment.length > 0;

    const hasAsns = hasAnyStaff || hasAnyEquipment;
    const hasCrewLead = staff.some((staffMember) => staffMember.isCrewLead);
    const hasFilledEquipment = !equipment.some((eq) => !eq.assignment);
    const readyForNotification = hasCrewLead || (hasFilledEquipment && !hasAnyStaff);

    const hasAnyPendingAssignments = staff.some((staffMember) => typeof staffMember.jobAssignmentId === 'string') || equipment.some((eq) => typeof eq.jobAssignmentId === 'string');

    const hasAllAssignmentsConfirmed = !(staff.concat(equipment)).some((resource) => resource.assignment?.confirmationStatusId !== ConfirmationStatus.Confirmed);

    const remainingDays = useMemo(() => {
        let days = 0;
        let i = day + 1;

        for (; i < 7; i += 1) {
            const dayAssignment = job.assignments[i];

            if (dayAssignment.staff.length > 0 || dayAssignment.equipment.length > 0) {
                days += 1;
            }
        }

        return days;
    }, [day, job]);

    const hasAutoAssignment = useMemo(
        () => [...(assignment?.staff ?? []), ...(assignment?.equipment ?? [])].some((r) => r.assignment?.isAutoAssignment),
        [assignment],
    );

    const status = useMemo(() => {
        //Emergency job
        if (canEdit && isEmergency) {
            return {
                color: 'text-danger-bs',
                label: 'Emergency',
                icon: faAmbulance,
            };
        }

        // Dispatch flagged
        if (canEdit && isFlagged) {
            const flagTooltipId = `day-flag-${jobId}-${day}`;

            return {
                color: 'text-danger-bs',
                label: (
                    <>
                        <span
                            id={flagTooltipId}
                            className="text-danger"
                        >
                            {flags.length > 1 ? pluralize(flags.length, 'Flag') : 'Flagged'}
                        </span>

                        <UncontrolledTooltip
                            placement="top"
                            target={flagTooltipId}
                        >
                            {flags.length === 0 ? (
                                'This job day has been flagged'
                            ) : (
                                <div className="text-left">
                                    <div className="font-weight-bold mb-1">
                                        {`${pluralize(flags.length, 'flag')} on this day:`}
                                    </div>

                                    {flags.map((flag, flagIndex) => (
                                        <div key={flag.flagId ?? flagIndex}>
                                            {flag.flagType}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </UncontrolledTooltip>
                    </>
                ),
                icon: faFlag,
            };
        }

        // Missing crew lead or it's a quick rental job without all slots filled
        if (canEdit && ((hasAnyStaff && !hasCrewLead) || (!hasAnyStaff && !hasFilledEquipment))) {
            return {
                color: 'text-warning-bs',
                label: hasAnyStaff ? `Missing ${crewLead}` : 'Missing Equipment',
                icon: hasAnyStaff ? faHardHat : faTrafficLight,
            };
        }

        // Timesheet complete!
        if (hasAsns && !canEdit) {
            return {
                color: 'text-success-bs',
                label: 'Timesheet Complete',
                icon: faCircleCheck,
            };
        }

        if (canEdit && hasAllAssignmentsConfirmed) {
            return {
                color: 'text-success-bs',
                label: 'Assignments Confirmed',
                icon: faCircleCheck,
            };
        }

        //Ready to go
        if (canEdit && readyForNotification) {
            return {
                color: 'text-primary-bs',
                label: hasCrewLead ? `${crewLead} Assigned` : 'Equipment Assigned',
                icon: faCircleCheck,
            };
        }

        return null;
    }, [jobId, flags, day, canEdit, hasAnyStaff, hasCrewLead, hasAsns, hasFilledEquipment, isEmergency, isFlagged, crewLead, hasAllAssignmentsConfirmed, readyForNotification]);

    const certs = useMemo(() => (job.certifications || []).filter((cert) => cert.day === day), [job.certifications, day]);
    const jobIsSelected = selectedJobId === jobId;

    const hasSameLocation = job.meetingAddress === job.jobAddress;

    return (
        <JobContext.Provider
            value={{
                job, day, ...assignment,
            }}
        >
            <JobSavingContext.Provider value={setSaving}>
                <div
                    className="shadow-sm rounded-lg position-relative w-100 mb-4 card-container"
                >
                    <Card className={clsx('d-flex flex-column rounded-lg position-relative', (saving || hasAnyPendingAssignments) && 'saving')}>
                        <Flex
                            alignItems="stretch"
                            className="w-100"
                        >
                            <div className="image-container flex-none position-relative">
                                <a
                                    rel="noopener noreferrer"
                                    title="Open in Google Maps"
                                    href={getMapUrl(job.jobAddress)}
                                    target="_blank"
                                >
                                    <CardImg
                                        src={getStaticMapUrlForAddress(location, { mapId: tenantSettings.mapId, apiKey: tenantSettings.googleMapsApiKey })}
                                        loading="lazy"
                                        className="h-100"
                                    />
                                </a>

                                {Boolean(job.jobTypeId) && (
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="pill rounded-pill text-truncate job-type-pill small"
                                            style={{
                                                backgroundColor: `${job.jobTypeDisplayColor}AA`,
                                                color: getContrastTextColor(job.jobTypeDisplayColor),
                                            }}
                                        >
                                            {job.jobTypeDescription}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <CardBody className="p-2 overflow-hidden w-100">
                                <div className="flex-fill d-flex text-truncate align-items-start">
                                    <div className="flex-fill text-truncate">
                                        <CardTitle className="mb-1 text-truncate">
                                            <Link
                                                className="job-number site-link"
                                                to={job.projectId ? `${AppNavPaths.Project}/${job.projectId}?openJob=${jobId}` : `${AppNavPaths.Job}/${jobId}`}
                                                target="_blank"
                                            >
                                                {jobNumber}
                                            </Link>
                                        </CardTitle>

                                        <CardSubtitle className="mb-1 text-truncate">
                                            <span>{job.name}</span>
                                        </CardSubtitle>

                                        <CardSubtitle className="mb-2 text-truncate text-muted">
                                            <span>{job.dispatchLocationName}</span>
                                        </CardSubtitle>
                                    </div>

                                    {remainingDays > 0 && (
                                        <div className="bg-secondary-subtle pill rounded-pill">
                                            +{remainingDays}d
                                        </div>
                                    )}
                                </div>

                                <FlexStartRow className="mb-2">
                                    <InfoItem
                                        icon={faUser}
                                        label={assignment.staff.length}
                                    />

                                    <InfoItem
                                        icon={faTruck}
                                        label={trucks.length}
                                    />

                                    <InfoItem
                                        icon={faTrafficLight}
                                        label={afads.length}
                                    />

                                    <InfoItem
                                        icon={faMapMarkerAlt}
                                        label={job.city}
                                    />
                                </FlexStartRow>

                                <FlexStartRow>
                                    {Boolean(status) && (
                                        <span className={clsx(status.color, 'small')}>
                                            <FontAwesomeIcon
                                                icon={status.icon}
                                                className="mr-1"
                                            />

                                            <strong>{status.label}</strong>
                                        </span>
                                    )}

                                    <div className="flex-fill" />

                                    <IconButton
                                        className="mr-2"
                                        onClick={() => setFieldValue('selectedJobId', jobIsSelected ? null : jobId)}
                                    >
                                        <FontAwesomeIcon icon={jobIsSelected ? faChevronUp : faChevronDown} />
                                    </IconButton>

                                    <UncontrolledDropdown className="mr-2">
                                        <DropdownToggle
                                            tag="span"
                                            data-toggle="dropdown"
                                            role="menu"
                                        >
                                            <IconButton
                                                type="button"
                                                size="lg"
                                            >
                                                <FontAwesomeIcon
                                                    icon={faEllipsisVertical}
                                                    className="text-dark"
                                                    size="lg"
                                                />
                                            </IconButton>
                                        </DropdownToggle>

                                        <DropdownMenu
                                            container={document.body}
                                            end
                                        >
                                            <DropdownItem
                                                onClick={() => setSelectedWorkflowJob({ job, day, assignments: assignment })}
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                                <span>Edit Job Workflow</span>
                                            </DropdownItem>

                                            {timesheetEquipmentAuditEnabled && Boolean(timesheetId) && (
                                                <Can
                                                    I="view"
                                                    a="timesheet_equipment_audit_log"
                                                >
                                                    <DropdownItem
                                                        onClick={() => viewEquipmentLog(timesheetId, job)}
                                                    >
                                                        <FontAwesomeIcon icon={faTruck} />
                                                        <span>View Equipment Log</span>
                                                    </DropdownItem>
                                                </Can>
                                            )}

                                            <Can
                                                I="edit"
                                                a="dispatch"
                                            >
                                                <FlagJob
                                                    day={selectedDate}
                                                    {...job}
                                                />

                                                {Boolean(canEdit && hasAsns) && (
                                                    <>
                                                        <CancelJob
                                                            day={day}
                                                            {...job}
                                                        />

                                                        <SetEmergency
                                                            day={day}
                                                            {...job}
                                                        />
                                                    </>
                                                )}
                                            </Can>

                                            <Can
                                                I="edit"
                                                a="dispatch_employee"
                                            >
                                                {Boolean(canEdit) && (
                                                    <TransferJob
                                                        day={day}
                                                        {...job}
                                                    />
                                                )}
                                            </Can>
                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                </FlexStartRow>
                            </CardBody>
                        </Flex>

                        <Collapse isOpen={jobIsSelected}>
                            {Boolean(jobIsSelected) && (
                                <CardFooter>
                                    <FormGroup>
                                        <FormLabel label="Location" />

                                        <div
                                            className="job-address mb-1"
                                            title="Job Location"
                                        >
                                            <Badge
                                                color="secondary"
                                                className="mr-1"
                                            >
                                                {hasSameLocation ? 'J, M' : 'J'}
                                            </Badge>

                                            {job.jobAddress}
                                        </div>

                                        {!hasSameLocation && (
                                            <div
                                                className="job-address mb-1"
                                                title="Meeting Location"
                                            >
                                                <Badge
                                                    color="secondary"
                                                    className="mr-1"
                                                >
                                                    M
                                                </Badge>

                                                {job.meetingAddress}
                                            </div>
                                        )}

                                        <div
                                            className="job-address"
                                            title="Job Time"
                                        >
                                            <FontAwesomeIcon
                                                icon={faClock}
                                                className="text-muted mr-1"
                                            />

                                            {appendTimeZone(job.startTime, job.startTimeOffset, job.timeZone)}
                                        </div>

                                        {Boolean(job.usCounty) && (
                                            <div
                                                className="job-address mt-1"
                                                title="Job County"
                                            >
                                                <Badge
                                                    color="secondary"
                                                    className="mr-1"
                                                >
                                                    C
                                                </Badge>

                                                <CountyLabel {...county} />
                                            </div>
                                        )}

                                        {job.hasCustomForms && (
                                            <CustomForms
                                                label="Job Forms"
                                                className="p-0 mt-1"
                                                isEmergency={job.isEmergency}
                                                jobId={jobId}
                                                companyId={job.companyId}
                                                formContextId={CustomFormContext.Job}
                                                onCompletionChange={(completed) => {
                                                    console.log({ completed });
                                                    // TODO - finish this
                                                }}
                                            />
                                        )}
                                    </FormGroup>

                                    {certs.length > 0 && (
                                        <FormGroup>
                                            <FormLabel label="Certifications" />
                                            <Flex>
                                                {certs.map((cert) => (
                                                    <Certification
                                                        key={cert.id}
                                                        {...cert}
                                                    />
                                                ))}
                                            </Flex>
                                        </FormGroup>
                                    )}

                                    <FormGroup>
                                        <FormLabel label="Foreman" />
                                        {job.foremanName}
                                    </FormGroup>

                                    {Boolean(job.jobTypeDescription) && (
                                        <FormGroup>
                                            <FormLabel label="Job Type" />
                                            <div className="d-flex align-items-center">
                                                {job.jobTypeDescription}

                                            </div>
                                        </FormGroup>
                                    )}

                                    <FormGroup className="mb-0">
                                        <div className="d-flex align-items-center">
                                            <FormLabel
                                                className="mb-2"
                                                label="Job Assignments"
                                            />
                                            {hasAutoAssignment && (
                                                <>
                                                    <FontAwesomeIcon
                                                        icon={faWandMagicSparkles}
                                                        id={`auto-assign-indicator-detail-${jobId}`}
                                                        className="ml-1 mb-2 text-warning"
                                                    />
                                                    <UncontrolledTooltip
                                                        target={`auto-assign-indicator-detail-${jobId}`}
                                                        placement="top"
                                                    >
                                                        One or more assignments on this job were created by Auto-Assign.
                                                    </UncontrolledTooltip>
                                                </>
                                            )}
                                        </div>
                                    </FormGroup>

                                    <div className="resources mt-2">
                                        {staff.map((group, index) => {
                                            const id = `staff_${jobId}_${day}_${index}`;

                                            return (
                                                <Employee
                                                    key={id}
                                                    id={id}
                                                    dispatchLocationId={job.dispatchLocationId}
                                                    canEdit={canEdit}
                                                    crewLead={crewLead}
                                                    {...group}
                                                />
                                            );
                                        })}

                                        {equipment.map((group, index) => {
                                            const id = `equipment_${jobId}_${day}_${index}`;

                                            return (
                                                <Equipment
                                                    key={id}
                                                    id={id}
                                                    assignmentIndex={index}
                                                    canEdit={canEdit}
                                                    dispatchLocationId={job.dispatchLocationId}
                                                    {...group}
                                                />
                                            );
                                        })}
                                    </div>

                                    <FlexEndRow className="mt-4">
                                        {/*}
                                        {remainingDays > 0 && (
                                            <>
                                                <StyledButton
                                                    id={`fill-${jobId}_${day}`}
                                                    color="primary"
                                                    endIcon={faAngleDoubleRight}
                                                    rounded
                                                    outlined
                                                    disabled={!readyForNotification}
                                                >
                                                    Fill {remainingDays} Remaining {pluralizeNoun('Day', remainingDays)}
                                                </StyledButton>

                                                {!readyForNotification && (
                                                    <UncontrolledTooltip target={`fill-${jobId}_${day}`}>
                                                        Make sure all slots are filled and a {crewLead} is assigned before filling the rest of the week.
                                                    </UncontrolledTooltip>
                                                )}
                                            </>
                                        )}
                                            {*/}
                                    </FlexEndRow>
                                </CardFooter>
                            )}
                        </Collapse>

                        <div className="saving-progress">
                            <FontAwesomeIcon
                                icon={faCircleNotch}
                                spin
                            />
                        </div>
                    </Card>
                </div>
            </JobSavingContext.Provider>
        </JobContext.Provider>
    );
};